import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Plus, Minus, Mountain, Coffee, Droplets, Sparkles, Check, Award, Info } from 'lucide-react';
import { useCartStore, formatCOP } from '../store/cartStore';
import { toast } from 'sonner';
import { PRODUCT } from '../constants/testIds';

const ProductSection = ({ product }) => {
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProfile, setSelectedProfile] = useState(product.profiles?.[0] || 'La Esperanza');
  const [selectedGrind, setSelectedGrind] = useState(product.grind_options?.[0] || 'En grano');
  const addItem = useCartStore(state => state.addItem);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  
  const handleAddToCart = () => {
    addItem(product, { profile: selectedProfile, grind: selectedGrind, quantity });
    toast.success(
      `${quantity}x ${selectedProfile} (${selectedGrind}) agregado al carrito`
    );
    setQuantity(1);
  };
  
  const incrementQuantity = () => setQuantity(q => q + 1);
  const decrementQuantity = () => setQuantity(q => Math.max(1, q - 1));
  
  const specs = [
    { 
      icon: Mountain, 
      label: 'Elevación', 
      value: product.specs?.elevation || product.elevation || 'N/A',
      description: 'Altura sobre el nivel del mar donde se cultiva el café'
    },
    { 
      icon: Coffee, 
      label: 'Variedad', 
      value: product.specs?.variety || product.variety || 'N/A',
      description: 'Tipo genético de la planta de café'
    },
    { 
      icon: Droplets, 
      label: 'Proceso', 
      value: product.specs?.process || product.process || 'N/A',
      description: 'Método de procesamiento del café tras la cosecha'
    },
    { 
      icon: Sparkles, 
      label: 'Tueste', 
      value: product.specs?.roast || product.roast || 'N/A',
      description: 'Nivel de tostado del grano'
    }
  ];
  
  const tabs = [
    { id: 'overview', label: 'Descripción' },
    { id: 'specs', label: 'Especificaciones' },
    { id: 'tasting', label: 'Notas de Cata' }
  ];
  
  const profileDescriptions = {
    'La Esperanza': 'Un perfil brillante con notas de panela, cítricos y naranja.',
    'Santa Teresa': 'Un perfil dulce y equilibrado con notas de panela, chocolate y naranja.'
  };
  
  const profileMeta = {
    'La Esperanza': 'SCA 85.5 · Acidez alta · Cuerpo medio',
    'Santa Teresa': 'SCA 85 · Acidez suave · Cuerpo alto'
  };
  
  const profileDetails = {
    'La Esperanza': {
      fullDescription: 'Un café de especialidad con una puntuación de 85.5 SCA, cultivado en las montañas del Huila. Destaca por sus notas de panela, cítricos y naranja, acompañadas de un cuerpo medio y una acidez viva que aporta frescura y complejidad en cada taza.',
      idealFor: 'Ideal para quienes disfrutan un café con una acidez más marcada, mayor vivacidad y un perfil brillante que resalta todas las características del origen.',
      notes: ['Panela', 'Cítricos', 'Naranja'],
      cupping: {
        fragancia: 'Afrutada con notas cítricas',
        acidez: 'Media alta',
        cuerpo: 'Medio',
        final: 'Limpio, jugoso y con notas a naranja'
      }
    },
    'Santa Teresa': {
      fullDescription: 'Un café de especialidad con una puntuación de 85 SCA, caracterizado por un cuerpo más pronunciado y una dulzura natural. Sus notas de panela, chocolate, limoncillo y naranja crean una taza equilibrada, limpia y de gran persistencia.',
      idealFor: 'Ideal para quienes prefieren una taza más suave, con menor acidez y un perfil redondo, perfecto para disfrutar todos los días.',
      notes: ['Panela', 'Chocolate', 'Limoncillo', 'Naranja'],
      cupping: {
        fragancia: 'Afrutada con especias',
        acidez: 'Media',
        cuerpo: 'Alto',
        final: 'Limpio'
      }
    }
  };
  
  const currentProfileDetails = profileDetails[selectedProfile] || profileDetails['La Esperanza'];
  
  const scaData = {
    'La Esperanza': {
      score: '85.5',
      description: 'Café de Especialidad certificado con una puntuación de 85.5 puntos SCA, reflejando una taza de excelente calidad con atributos sobresalientes.'
    },
    'Santa Teresa': {
      score: '85',
      description: 'Café de Especialidad certificado con una puntuación de 85 puntos SCA, reconocido por su equilibrio, dulzura y excelente calidad en taza.'
    }
  };
  
  const currentSca = scaData[selectedProfile] || scaData['La Esperanza'];
  
  return (
    <section id="cafe" className="py-24 bg-[#FDFBF7] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-[#B58A3F] uppercase tracking-[0.3em] text-sm mb-4 font-medium">
            {product.origin}
          </p>
          <h2 className="heading-serif text-4xl md:text-5xl text-[#142A1D] tracking-tight font-light">
            {product.region || 'Nuestro Café'}
          </h2>
        </motion.div>
        
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Product Image with Interactive Specs */}
          <motion.div 
            data-testid={PRODUCT.card}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:sticky lg:top-24"
          >
            <div className="relative group">
              <motion.div 
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="aspect-square rounded-3xl overflow-hidden bg-[#0a0a0a] shadow-2xl"
              >
                <img
                  data-testid={PRODUCT.image}
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              </motion.div>
              
              {/* Floating Spec Cards */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                {specs.map((spec, index) => {
                  const Icon = spec.icon;
                  return (
                    <motion.div
                      key={spec.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={inView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                      whileHover={{ y: -4, scale: 1.02 }}
                      className="group/spec bg-white rounded-2xl p-4 border border-[#E5DEC5] hover:border-[#B58A3F] transition-all cursor-pointer shadow-sm hover:shadow-lg relative overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-[#B58A3F]/5 to-transparent opacity-0 group-hover/spec:opacity-100 transition-opacity" />
                      <div className="relative">
                        <Icon className="w-5 h-5 text-[#B58A3F] mb-2" strokeWidth={1.5} />
                        <p className="text-xs text-[#142A1D] opacity-60 uppercase tracking-wider mb-1">
                          {spec.label}
                        </p>
                        <p className="text-sm font-medium text-[#142A1D]">{spec.value}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
          
          {/* Product Details */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-8"
          >
            <div>
              <h1 
                data-testid={PRODUCT.name}
                className="heading-serif text-5xl md:text-6xl text-[#142A1D] mb-4 tracking-tight font-light"
              >
                {product.name}
              </h1>
              <div className="flex items-baseline gap-4 mb-2">
                <p 
                  data-testid={PRODUCT.price}
                  className="text-4xl text-[#B58A3F] font-light heading-serif"
                >
                  {formatCOP(product.price)}
                </p>
                <p className="text-lg text-[#142A1D] opacity-60">
                  {product.weight}
                </p>
              </div>
              {product.producer && (
                <p className="text-[#142A1D] opacity-70 text-sm">
                  Productor: {product.producer}
                </p>
              )}
            </div>
            
            {/* SCA Badge - Especialidad */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedProfile}
                initial={{ opacity: 0, y: 15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -15, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                data-testid="sca-badge"
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl border-2 border-[#B58A3F] bg-gradient-to-br from-[#142A1D] via-[#1e3a2a] to-[#2B4C36] p-5 shadow-lg">
                  {/* Decorative corner ornaments */}
                  <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 border-[#B58A3F] opacity-60" />
                  <div className="absolute top-2 right-2 w-6 h-6 border-t-2 border-r-2 border-[#B58A3F] opacity-60" />
                  <div className="absolute bottom-2 left-2 w-6 h-6 border-b-2 border-l-2 border-[#B58A3F] opacity-60" />
                  <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 border-[#B58A3F] opacity-60" />
                  
                  {/* Subtle gold shimmer */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#B58A3F]/5 to-transparent" />
                  
                  <div className="relative flex items-center gap-4">
                    {/* Award icon with coffee bean */}
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-[#B58A3F] to-[#8B6928] flex items-center justify-center shadow-lg">
                      <Award className="w-7 h-7 text-[#FDFBF7]" strokeWidth={1.5} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Coffee className="w-3.5 h-3.5 text-[#B58A3F]" strokeWidth={2} />
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#B58A3F] font-medium">
                          Café de Especialidad
                        </p>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="heading-serif text-3xl text-[#FDFBF7] font-light">
                          SCA
                        </span>
                        <motion.span
                          key={currentSca.score}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                          data-testid="sca-score"
                          className="heading-serif text-4xl text-[#B58A3F] font-medium tabular-nums"
                        >
                          {currentSca.score}
                        </motion.span>
                      </div>
                    </div>
                    
                    {/* Info tooltip */}
                    <div className="group/info relative flex-shrink-0">
                      <button
                        data-testid="sca-info-button"
                        aria-label="Qué significa SCA"
                        className="w-8 h-8 rounded-full border border-[#B58A3F]/50 flex items-center justify-center hover:bg-[#B58A3F]/20 transition-colors"
                      >
                        <Info className="w-4 h-4 text-[#B58A3F]" strokeWidth={1.5} />
                      </button>
                      
                      {/* Tooltip */}
                      <div className="absolute right-0 top-full mt-2 w-72 p-4 bg-[#FDFBF7] border border-[#E5DEC5] rounded-xl shadow-2xl opacity-0 pointer-events-none group-hover/info:opacity-100 group-hover/info:pointer-events-auto transition-opacity duration-300 z-20"
                           data-testid="sca-tooltip">
                        <div className="flex items-center gap-2 mb-2">
                          <Award className="w-4 h-4 text-[#B58A3F]" strokeWidth={1.5} />
                          <p className="text-xs uppercase tracking-widest text-[#B58A3F] font-medium">
                            ¿Qué significa SCA?
                          </p>
                        </div>
                        <p className="text-xs text-[#142A1D] leading-relaxed">
                          <strong>SCA (Specialty Coffee Association)</strong> es el estándar internacional para evaluar cafés de especialidad. Los cafés que obtienen 80 puntos o más son considerados cafés de especialidad. Nuestros perfiles alcanzan 85 y 85.5 puntos, ubicándolos dentro de la categoría de cafés de excelente calidad.
                        </p>
                        {/* Tooltip arrow */}
                        <div className="absolute -top-2 right-3 w-4 h-4 bg-[#FDFBF7] border-t border-l border-[#E5DEC5] transform rotate-45" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Supporting description */}
                  <p 
                    data-testid="sca-description"
                    className="relative text-xs text-[#F2EBE1] opacity-80 mt-3 pt-3 border-t border-[#B58A3F]/20 leading-relaxed italic"
                  >
                    {currentSca.description}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
            
            {/* Profile Selection */}
            <div data-testid="profile-selector">
              <div className="flex items-center justify-between mb-3">
                <label className="text-[#142A1D] font-medium uppercase tracking-wider text-sm">
                  Perfil de Sabor
                </label>
                <span className="text-xs text-[#B58A3F] font-medium">
                  {selectedProfile}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {product.profiles?.map((profile) => (
                  <motion.button
                    key={profile}
                    data-testid={`profile-option-${profile.toLowerCase().replace(' ', '-')}`}
                    onClick={() => setSelectedProfile(profile)}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                      selectedProfile === profile
                        ? 'border-[#B58A3F] bg-gradient-to-br from-[#B58A3F]/10 to-transparent shadow-md'
                        : 'border-[#E5DEC5] bg-white hover:border-[#B58A3F]/50'
                    }`}
                  >
                    {selectedProfile === profile && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-3 right-3 w-5 h-5 bg-[#B58A3F] rounded-full flex items-center justify-center"
                      >
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </motion.div>
                    )}
                    <p className="font-medium text-[#142A1D] text-base mb-1">
                      {profile}
                    </p>
                    <p className="text-xs text-[#142A1D] opacity-70 leading-relaxed mb-2">
                      {profileDescriptions[profile]}
                    </p>
                    <p className="text-[10px] text-[#B58A3F] font-medium uppercase tracking-wider">
                      {profileMeta[profile]}
                    </p>
                  </motion.button>
                ))}
              </div>
            </div>
            
            {/* Grind Selection */}
            <div data-testid="grind-selector">
              <div className="flex items-center justify-between mb-3">
                <label className="text-[#142A1D] font-medium uppercase tracking-wider text-sm">
                  Tipo de Molienda
                </label>
                <span className="text-xs text-[#B58A3F] font-medium">
                  {selectedGrind}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {product.grind_options?.map((grind) => (
                  <motion.button
                    key={grind}
                    data-testid={`grind-option-${grind.toLowerCase().replace(' ', '-')}`}
                    onClick={() => setSelectedGrind(grind)}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-4 rounded-xl border-2 transition-all ${
                      selectedGrind === grind
                        ? 'border-[#B58A3F] bg-gradient-to-br from-[#B58A3F]/10 to-transparent shadow-md'
                        : 'border-[#E5DEC5] bg-white hover:border-[#B58A3F]/50'
                    }`}
                  >
                    {selectedGrind === grind && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-3 right-3 w-5 h-5 bg-[#B58A3F] rounded-full flex items-center justify-center"
                      >
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </motion.div>
                    )}
                    <p className="font-medium text-[#142A1D] text-base">
                      {grind}
                    </p>
                    <p className="text-xs text-[#142A1D] opacity-60 mt-1">
                      {grind === 'En grano' ? 'Ideal para molinos' : 'Listo para preparar'}
                    </p>
                  </motion.button>
                ))}
              </div>
            </div>
            
            {/* Tabs */}
            <div>
              <div className="flex gap-2 border-b border-[#E5DEC5] mb-6">
                {tabs.map((tab) => (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                      activeTab === tab.id
                        ? 'text-[#B58A3F]'
                        : 'text-[#142A1D] opacity-60 hover:opacity-100'
                    }`}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#B58A3F]"
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    )}
                  </motion.button>
                ))}
              </div>
              
              {/* Tab Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="min-h-[180px]"
                >
                  {activeTab === 'overview' && (
                    <div data-testid={PRODUCT.description} className="space-y-4">
                      <p className="text-[#142A1D] opacity-90 text-lg leading-relaxed">
                        {currentProfileDetails.fullDescription}
                      </p>
                      <p className="text-[#B58A3F] text-base leading-relaxed italic font-medium border-l-2 border-[#B58A3F] pl-4">
                        {currentProfileDetails.idealFor}
                      </p>
                    </div>
                  )}
                  
                  {activeTab === 'specs' && (
                    <div className="space-y-4">
                      {specs.map((spec, index) => {
                        const Icon = spec.icon;
                        return (
                          <motion.div
                            key={spec.label}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-start gap-4 p-4 rounded-xl bg-[#F2EBE1] border border-[#E5DEC5]"
                          >
                            <Icon className="w-6 h-6 text-[#B58A3F] flex-shrink-0 mt-1" strokeWidth={1.5} />
                            <div>
                              <p className="font-medium text-[#142A1D] mb-1">{spec.label}: {spec.value}</p>
                              <p className="text-sm text-[#142A1D] opacity-70">{spec.description}</p>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                  
                  {activeTab === 'tasting' && (
                    <div data-testid={PRODUCT.tastingNotes} className="space-y-6">
                      {/* Notas principales */}
                      <div>
                        <p className="text-[#142A1D] opacity-80 mb-4 text-sm uppercase tracking-wider font-medium">
                          Notas Principales
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {currentProfileDetails.notes.map((note, index) => (
                            <motion.div
                              key={`${selectedProfile}-${note}`}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.08 }}
                              whileHover={{ scale: 1.05, rotate: 2 }}
                              className="px-6 py-4 bg-gradient-to-br from-[#B58A3F]/10 to-[#2B4C36]/10 text-[#142A1D] rounded-2xl text-center font-medium border-2 border-[#E5DEC5] hover:border-[#B58A3F] transition-all shadow-sm"
                            >
                              {note}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Perfil de Cata */}
                      <div>
                        <p className="text-[#142A1D] opacity-80 mb-4 text-sm uppercase tracking-wider font-medium">
                          Perfil de Cata
                        </p>
                        <div className="space-y-2">
                          {Object.entries(currentProfileDetails.cupping).map(([key, value], index) => (
                            <motion.div
                              key={`${selectedProfile}-${key}`}
                              initial={{ opacity: 0, x: -15 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.08 }}
                              className="flex items-start justify-between gap-4 p-3 rounded-xl bg-[#F2EBE1] border border-[#E5DEC5]"
                            >
                              <span className="text-sm text-[#142A1D] opacity-70 capitalize font-medium min-w-[100px]">
                                {key}
                              </span>
                              <span className="text-sm text-[#142A1D] font-medium text-right flex-1">
                                {value}
                              </span>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Add to Cart */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 items-start sm:items-center pt-6 border-t border-[#E5DEC5]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center gap-3 bg-white rounded-xl p-2 border-2 border-[#E5DEC5]">
                <motion.button
                  onClick={decrementQuantity}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-3 hover:bg-[#F2EBE1] rounded-lg transition-colors"
                  aria-label="Disminuir cantidad"
                >
                  <Minus className="w-5 h-5 text-[#142A1D]" strokeWidth={2.5} />
                </motion.button>
                <span 
                  data-testid={PRODUCT.quantityInput}
                  className="w-16 text-center font-medium text-[#142A1D] text-lg"
                >
                  {quantity}
                </span>
                <motion.button
                  onClick={incrementQuantity}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-3 hover:bg-[#F2EBE1] rounded-lg transition-colors"
                  aria-label="Aumentar cantidad"
                >
                  <Plus className="w-5 h-5 text-[#142A1D]" strokeWidth={2.5} />
                </motion.button>
              </div>
              
              <motion.button
                data-testid={PRODUCT.addToCartButton}
                onClick={handleAddToCart}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="btn-primary flex-1 sm:flex-none px-8 py-4 text-lg"
              >
                Agregar al Carrito
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProductSection;
